import cellsFile from 'virtual:sao-cells';

/**
 * The market cells this portal build can talk to. The list comes from
 * cells.json at the repo root (docs/workflows/market-cell.md), reduced at
 * build time to code, name and the two backend URLs (cells-plugin.ts), so no
 * project ref is written in portal code and no environment name reaches the bundle. A cell's publishable key, and any address
 * that differs from cells.json, come from the build's environment:
 *
 *   home cell   VITE_SUPABASE_URL, VITE_SUPABASE_PUBLISHABLE_KEY,
 *               VITE_ADMIN_API_URL, VITE_ENV_LABEL (as before)
 *   other cell  VITE_SUPABASE_URL_<CODE> (default: its staging project when the
 *               label is not Production, else its production backend),
 *               VITE_SUPABASE_PUBLISHABLE_KEY_<CODE>,
 *               VITE_ADMIN_API_URL_<CODE>, VITE_ENV_LABEL_<CODE>
 *
 * The pill reads Production unless the build says otherwise, so a forgotten
 * variable can only make a portal look more serious, never less.
 */
export interface CellEntry {
  code: string;
  name: string;
  productionUrl: string;
  stagingUrl: string;
}

export interface CellsFile {
  home: string;
  cells: readonly CellEntry[];
}

export type BuildEnv = Readonly<Record<string, unknown>>;

export interface PortalCell {
  code: string;
  name: string;
  /** Supabase URL; undefined for a home cell the build did not configure. */
  url: string | undefined;
  /** The publishable key; the portal never holds anything secret. */
  key: string | undefined;
  /** An admin-api served some other way, or undefined for the project's own. */
  adminApiUrl: string | undefined;
  envLabel: string;
  /** Where supabase-js keeps this cell's session: one per cell. */
  storageKey: string;
}

export const CELL_STORAGE_KEY = 'sao-staff-cell';

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

export function buildCell(
  entry: CellEntry,
  home: string,
  env: BuildEnv,
): PortalCell {
  const isHome = entry.code === home;
  const suffix = isHome ? '' : `_${entry.code}`;
  const envLabel = text(env[`VITE_ENV_LABEL${suffix}`]) ?? 'Production';
  // Another cell's backend follows its label: a staging or preview build opens
  // the staging project, Production the production one. An override wins, and
  // buildCells then checks it against the label.
  const fallback =
    envLabel === 'Production' ? entry.productionUrl : entry.stagingUrl;
  return {
    code: entry.code,
    name: entry.name,
    // The home cell keeps its existing variables, so its build is unchanged.
    url: isHome
      ? text(env.VITE_SUPABASE_URL)
      : (text(env[`VITE_SUPABASE_URL${suffix}`]) ?? fallback),
    key: text(env[`VITE_SUPABASE_PUBLISHABLE_KEY${suffix}`]),
    adminApiUrl: text(env[`VITE_ADMIN_API_URL${suffix}`]),
    envLabel,
    storageKey: isHome ? 'sao-staff-auth' : `sao-staff-auth-${entry.code}`,
  };
}

function hostOf(url: string | undefined): string | null {
  try {
    return url ? new URL(url).hostname : null;
  } catch {
    return null;
  }
}

/**
 * Why a cell other than home must not be used, or null: a label that is not
 * Production on the cell's production backend (a staging build writing to real
 * data), or Production on its staging backend (a live pill over test data).
 */
export function mismatch(cell: PortalCell, entry: CellEntry): string | null {
  const host = hostOf(cell.url);
  const production = cell.envLabel === 'Production';
  if (!production && host === hostOf(entry.productionUrl)) {
    return `labelled ${cell.envLabel} but pointed at its production backend`;
  }
  if (production && host === hostOf(entry.stagingUrl)) {
    return 'labelled Production but pointed at its staging backend';
  }
  return null;
}

export function buildCells(file: CellsFile, env: BuildEnv): PortalCell[] {
  return file.cells.flatMap((entry) => {
    const cell = buildCell(entry, file.home, env);
    const problem = entry.code === file.home ? null : mismatch(cell, entry);
    if (problem) {
      console.warn(`Portal cell ${entry.code} left out: ${problem}.`);
      return [];
    }
    return [cell];
  });
}

/** The stored cell if it is still in the list, otherwise the home cell. */
export function chooseCell(
  cells: readonly PortalCell[],
  home: string,
  stored: string | null,
): PortalCell {
  const found =
    cells.find((cell) => cell.code === stored) ??
    cells.find((cell) => cell.code === home) ??
    cells[0];
  if (!found) throw new Error('cells.json lists no cell.');
  return found;
}

/**
 * The cell a link names (`?cell=LR`): one portal serves every cell, so an
 * invite, approval or export link from another cell's admin-api says which
 * backend to open. Only a listed cell counts, and opening it keeps it chosen.
 */
export function readLinkCell(
  cells: readonly PortalCell[],
  search: string,
): string | null {
  const code = new URLSearchParams(search).get('cell');
  // The listed cell's own code, never the link's text, is what gets stored.
  return cells.find((cell) => cell.code === code)?.code ?? null;
}

export function readStoredCell(): string | null {
  try {
    return localStorage.getItem(CELL_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function storeCell(code: string): void {
  try {
    localStorage.setItem(CELL_STORAGE_KEY, code);
  } catch {
    // Without storage the portal opens on the home cell.
  }
}

export const HOME_CELL_CODE = cellsFile.home;
export const CELLS: readonly PortalCell[] = buildCells(
  cellsFile,
  import.meta.env,
);
const LINK_CELL = readLinkCell(CELLS, globalThis.location?.search ?? '');
if (LINK_CELL) storeCell(LINK_CELL);
/**
 * The cell this tab is signed in to, fixed for the life of the page: the one a
 * link names (an invite into a country that is off still works), else the
 * stored choice, else home. The picker, not this, decides what else is offered.
 */
export const ACTIVE_CELL: PortalCell = chooseCell(
  CELLS,
  HOME_CELL_CODE,
  LINK_CELL ?? readStoredCell(),
);
/** The picker shows only when there is a choice to make. */
export const HAS_CELL_PICKER = CELLS.length > 1;
